import { useState, type JSX } from 'react'
import { UserPlus, X } from 'lucide-react'

interface Props {
  waId: string
  initialName: string
  initialEmail: string
  onClose: () => void
  onSave: (payload: { fullName: string; email?: string }) => Promise<void>
}

/**
 * Saves a WhatsApp contact into the tenant's clients table. The API files it
 * under the client group bound to the whatsapp channel.
 */
export function SaveContactDialog({
  waId,
  initialName,
  initialEmail,
  onClose,
  onSave,
}: Props): JSX.Element {
  const [fullName, setFullName] = useState(initialName)
  const [email, setEmail] = useState(initialEmail)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  async function handleSave(): Promise<void> {
    if (!fullName.trim()) {
      setError('A name is required.')
      return
    }
    setSaving(true)
    setError(null)
    try {
      await onSave({ fullName: fullName.trim(), ...(email.trim() ? { email: email.trim() } : {}) })
    } catch {
      setError('Could not save the contact. The email may already belong to another client.')
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="w-full max-w-sm overflow-hidden rounded-xl bg-white shadow-xl">
        <div className="flex items-center gap-2 border-b border-gray-200 px-5 py-3">
          <UserPlus className="h-4 w-4 text-[#128C7E]" />
          <h3 className="flex-1 text-sm font-semibold text-gray-800">Add to contacts</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="space-y-3 px-5 py-4">
          <div>
            <label className="mb-1 block text-[11px] font-medium text-gray-500">WhatsApp number</label>
            <input
              type="text"
              value={`+${waId}`}
              disabled
              className="w-full rounded-md border border-gray-200 bg-gray-50 px-2.5 py-1.5 text-xs text-gray-500"
            />
          </div>
          <div>
            <label className="mb-1 block text-[11px] font-medium text-gray-500">Full name *</label>
            <input
              type="text"
              value={fullName}
              onChange={(e) => setFullName(e.target.value)}
              placeholder="Jane Doe"
              className="w-full rounded-md border border-gray-200 px-2.5 py-1.5 text-xs outline-none focus:border-[#25D366] focus:ring-1 focus:ring-[#25D366]/20"
            />
          </div>
          <div>
            <label className="mb-1 block text-[11px] font-medium text-gray-500">Email</label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder={`wa-${waId}@whatsapp.local`}
              className="w-full rounded-md border border-gray-200 px-2.5 py-1.5 text-xs outline-none focus:border-[#25D366] focus:ring-1 focus:ring-[#25D366]/20"
            />
            <p className="mt-1 text-[10px] text-gray-400">
              Left blank, a placeholder address is generated — clients records require one.
            </p>
          </div>
          {error && <p className="text-[11px] text-red-600">{error}</p>}
        </div>

        <div className="flex items-center justify-end gap-2 border-t border-gray-200 px-5 py-3">
          <button
            onClick={onClose}
            className="rounded-md border border-gray-200 px-3 py-1.5 text-xs text-gray-600 hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            onClick={() => void handleSave()}
            disabled={saving}
            className="rounded-md bg-[#25D366] px-4 py-1.5 text-xs font-medium text-white transition-colors hover:bg-[#128C7E] disabled:opacity-40"
          >
            {saving ? 'Saving…' : 'Save contact'}
          </button>
        </div>
      </div>
    </div>
  )
}
